/**
 * Where each control stands on the firm's self-assessment worklist (DEC-21, DEC-31). One rule, used by the API for
 * the dashboard and Home counts and by the worklist itself, so the counts and the list always agree.
 */
export const WORK_STATES = ['returned', 'not_rated', 'draft', 'awaiting', 'reviewed', 'cdw'];
const time = (d) => (d === null ? null : new Date(d).getTime());
/** The control's state, in the order an IT lead acts on it. */
export function workState(c) {
    if (c.reviewState === 'returned')
        return 'returned';
    if (c.reviewState === 'submitted')
        return 'awaiting';
    // A rating saved and never sent (DEC-31), including one changed after National's last review.
    const self = time(c.selfUpdatedAt);
    const reviewed = time(c.reviewedAt);
    if (c.selfStatus && self !== null && reviewed !== null && self > reviewed)
        return 'draft';
    if (reviewed !== null || c.currentSource === 'reviewed')
        return 'reviewed';
    if (c.selfStatus)
        return 'draft';
    if (c.cdwStatus)
        return 'cdw';
    return 'not_rated';
}
/** How many controls are in each state. */
export function workCounts(rows) {
    const n = Object.fromEntries(WORK_STATES.map((s) => [s, 0]));
    for (const r of rows)
        n[workState(r)] += 1;
    return n;
}
/**
 * The order rows are listed in: what the IT lead should act on first. CDW results that are not yet or only partly in
 * place come before reviewed and waiting controls; CDW controls already in place come last.
 */
export function workRank(c) {
    const s = workState(c);
    if (s === 'returned')
        return 0;
    if (s === 'draft')
        return 1;
    if (s === 'not_rated')
        return 2;
    if (s === 'cdw')
        return c.cdwStatus === 'gap' ? 3 : c.cdwStatus === 'partial_gap' ? 4 : 7;
    if (s === 'reviewed')
        return 5;
    return 6; // awaiting: nothing for the firm to do until National reviews it
}
/**
 * Where a control sits once the worklist is narrowed (a wave, a status, an area, a search).
 * Not yet in place, then no 2026 rating (March 2025 "not reported in place" first), then partly, then fully.
 * Out of scope sits with fully. workRank still decides what to do inside that group. This does not change workRank,
 * and the dashboard counts do not use it.
 */
export function placeRank(currentStatus, surveyStatus = null) {
    if (currentStatus === 'gap')
        return 0;
    if (currentStatus == null) {
        if (surveyStatus === 'not_achieved')
            return 1;
        if (surveyStatus === 'achieved')
            return 3;
        return 2;
    }
    if (currentStatus === 'partial_gap')
        return 4;
    return 5;
}
export function wave2Band(controls) {
    const wave = controls.filter((c) => c.btiWave === 2);
    const notYet = wave.filter((c) => c.currentStatus === 'gap').length;
    const unrated = wave.filter((c) => c.currentStatus == null).length;
    const inPlace = wave.filter((c) => c.currentStatus === 'no_gap' || c.currentStatus === 'partial_gap').length;
    const total = wave.length;
    const action = notYet > 0 ? 'gaps' : unrated === total ? 'wave' : inPlace === total ? 'review' : 'wave';
    return { action, notYet, unrated, total };
}
/** The band's sentence. `dueLabel` is the calendar date already formatted for the page. */
export function wave2Sentence(band, dueLabel, wasDue) {
    if (band.unrated === band.total)
        return `These ${band.total} have no 2026 rating yet.`;
    if (band.action === 'review')
        return `All ${band.total} Wave 2 controls are in place, fully or partly.`;
    const due = `Baker Tilly International's Wave 2 ${wasDue ? 'was' : 'is'} due ${dueLabel}.`;
    const gap = `${band.notYet} of ${band.total} ${band.notYet === 1 ? 'is' : 'are'} not yet in place.`;
    const more = band.unrated === 0 ? '' : ` ${band.unrated} ${band.unrated === 1 ? 'has' : 'have'} no 2026 rating yet.`;
    return `${due} ${gap}${more}`;
}
//# sourceMappingURL=worklist.js.map